import { useQuery } from "@tanstack/react-query"
import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import {
  type MarketSector,
  type ProjectMeta,
  trpcClient,
} from "@tokenterminal/core/utils/trpc"
import { useMemo } from "react"

const DISPLAY_MARKET_SECTORS = [
  "stablecoin-issuers",
  "blockchains-l1",
  "exchange",
  "lending",
  "liquid-staking",
  "derivatives",
] as const

const METRIC_ID = "fees"

interface MetricsBreakdownItem {
  data_id: string
  chain_id?: string
  market_sector_id?: string
  metrics: Record<
    string,
    {
      avg: number
      trend: number
      change: number
      sum?: number
      latest?: number
    }
  >
}

interface TreemapProject {
  id: string
  name: string
  logo: string
  slug: string
  value: number
  percentOfTotal: number
  change: number
}

interface TreemapSector {
  id: string
  name: string
  projects: TreemapProject[]
  totalValue: number
  percentOfTotal: number
}

const FALLBACK_SECTORS: TreemapSector[] = [
  {
    id: "stablecoin-issuers",
    name: "Stablecoin issuers",
    totalValue: 671500000,
    percentOfTotal: 41.6,
    projects: [
      {
        id: "tether",
        name: "Tether",
        logo: "tether/logo.png",
        slug: "tether",
        value: 417900000,
        percentOfTotal: 25.9,
        change: 5.2,
      },
      {
        id: "circle",
        name: "Circle",
        logo: "circle/logo.png",
        slug: "circle",
        value: 197100000,
        percentOfTotal: 12.2,
        change: 3.1,
      },
      {
        id: "makerdao",
        name: "Sky",
        logo: "makerdao/logo.png",
        slug: "makerdao",
        value: 33200000,
        percentOfTotal: 2.1,
        change: -1.5,
      },
      {
        id: "ethena",
        name: "Ethena",
        logo: "ethena/logo.png",
        slug: "ethena",
        value: 23300000,
        percentOfTotal: 1.4,
        change: 8.3,
      },
    ],
  },
  {
    id: "blockchains-l1",
    name: "Blockchains (L1)",
    totalValue: 456200000,
    percentOfTotal: 28.3,
    projects: [
      {
        id: "tron",
        name: "Tron",
        logo: "tron/logo.png",
        slug: "tron",
        value: 207800000,
        percentOfTotal: 12.9,
        change: 2.1,
      },
      {
        id: "zcash",
        name: "Zcash",
        logo: "zcash/logo.png",
        slug: "zcash",
        value: 124100000,
        percentOfTotal: 7.7,
        change: -0.8,
      },
      {
        id: "solana",
        name: "Solana",
        logo: "solana/logo.png",
        slug: "solana",
        value: 89300000,
        percentOfTotal: 5.5,
        change: 12.4,
      },
      {
        id: "bsc",
        name: "BNB Chain",
        logo: "bsc/logo.png",
        slug: "bsc",
        value: 35000000,
        percentOfTotal: 2.2,
        change: 1.9,
      },
    ],
  },
  {
    id: "exchange",
    name: "Exchanges (DEX)",
    totalValue: 125700000,
    percentOfTotal: 7.8,
    projects: [
      {
        id: "pancakeswap",
        name: "PancakeSwap",
        logo: "pancakeswap/logo.png",
        slug: "pancakeswap",
        value: 48900000,
        percentOfTotal: 3.0,
        change: -2.3,
      },
      {
        id: "pumpfun",
        name: "pump.fun",
        logo: "pumpfun/logo.png",
        slug: "pumpfun",
        value: 38400000,
        percentOfTotal: 2.4,
        change: 45.2,
      },
      {
        id: "uniswap",
        name: "Uniswap",
        logo: "uniswap/logo.png",
        slug: "uniswap",
        value: 38400000,
        percentOfTotal: 2.4,
        change: 1.1,
      },
    ],
  },
  {
    id: "lending",
    name: "Lending",
    totalValue: 115600000,
    percentOfTotal: 7.2,
    projects: [
      {
        id: "aave",
        name: "Aave",
        logo: "aave/logo.png",
        slug: "aave",
        value: 64400000,
        percentOfTotal: 4.0,
        change: 6.7,
      },
      {
        id: "morpho",
        name: "Morpho",
        logo: "morpho/logo.png",
        slug: "morpho",
        value: 28600000,
        percentOfTotal: 1.8,
        change: 15.3,
      },
      {
        id: "maple",
        name: "Maple",
        logo: "maple/logo.png",
        slug: "maple",
        value: 22600000,
        percentOfTotal: 1.4,
        change: 3.2,
      },
    ],
  },
  {
    id: "liquid-staking",
    name: "Liquid staking",
    totalValue: 129200000,
    percentOfTotal: 8.0,
    projects: [
      {
        id: "lido",
        name: "Lido Finance",
        logo: "lido/logo.png",
        slug: "lido",
        value: 78000000,
        percentOfTotal: 4.8,
        change: -1.2,
      },
      {
        id: "jito",
        name: "Jito",
        logo: "jito/logo.png",
        slug: "jito",
        value: 28200000,
        percentOfTotal: 1.7,
        change: 22.1,
      },
      {
        id: "etherfi",
        name: "ether.fi",
        logo: "etherfi/logo.png",
        slug: "etherfi",
        value: 23000000,
        percentOfTotal: 1.4,
        change: 18.5,
      },
    ],
  },
  {
    id: "derivatives",
    name: "Derivatives",
    totalValue: 51200000,
    percentOfTotal: 3.2,
    projects: [
      {
        id: "hyperliquid",
        name: "Hyperliquid",
        logo: "hyperliquid/logo.png",
        slug: "hyperliquid",
        value: 25600000,
        percentOfTotal: 1.6,
        change: 35.2,
      },
      {
        id: "gmx",
        name: "GMX",
        logo: "gmx/logo.png",
        slug: "gmx",
        value: 15600000,
        percentOfTotal: 1.0,
        change: -5.1,
      },
    ],
  },
  {
    id: "other",
    name: "Other",
    totalValue: 62400000,
    percentOfTotal: 3.9,
    projects: [
      {
        id: "phantom",
        name: "Phantom",
        logo: "phantom/logo.png",
        slug: "phantom",
        value: 32400000,
        percentOfTotal: 2.0,
        change: 8.9,
      },
      {
        id: "flashbots",
        name: "Flashbots",
        logo: "flashbots/logo.png",
        slug: "flashbots",
        value: 18000000,
        percentOfTotal: 1.1,
        change: 2.3,
      },
    ],
  },
]

export function useTreemapData() {
  const {
    data: marketSectorsData,
    isLoading: isMarketSectorsLoading,
    isError: isMarketSectorsError,
  } = useQuery({
    queryKey: ["market-sectors-list"],
    queryFn: async () => {
      const result = await trpcClient.marketSectors.getMarketSectors.query()
      return result.data
    },
    staleTime: 1000 * 60 * 30,
    retry: 2,
  })

  const {
    data: projectsData,
    isLoading: isProjectsLoading,
    isError: isProjectsError,
  } = useQuery({
    queryKey: ["projects-list"],
    queryFn: async () => {
      const result = await trpcClient.projects.getProjects.query({
        include_placeholders: false,
      })
      return result.data
    },
    staleTime: 1000 * 60 * 10,
    retry: 2,
  })

  const {
    data: feesBreakdownData,
    isLoading: isFeesLoading,
    isError: isFeesError,
  } = useQuery({
    queryKey: ["treemap-fees-breakdown", METRIC_ID, "30d"],
    queryFn: async () => {
      const result = await trpcClient.metrics.postBreakdown.mutate({
        metric_ids: [METRIC_ID],
        interval: "30d",
        groupBy: "projects",
        ignore_threshold: true,
      })
      return result.data as MetricsBreakdownItem[]
    },
    staleTime: 1000 * 60 * 5,
    retry: 2,
  })

  const isLoading = isMarketSectorsLoading || isProjectsLoading || isFeesLoading
  const hasError = isMarketSectorsError || isProjectsError || isFeesError

  const projectsMap = useMemo(() => {
    if (!projectsData) return new Map<string, ProjectMeta>()
    return toDictionary(projectsData, "data_id")
  }, [projectsData])

  const marketSectorsMap = useMemo(() => {
    if (!marketSectorsData) return new Map<string, MarketSector>()
    return toDictionary(marketSectorsData, "slug")
  }, [marketSectorsData])

  const { sectors, totalValue } = useMemo(() => {
    if (!feesBreakdownData || !projectsData || !marketSectorsData) {
      return { sectors: [], totalValue: 0 }
    }

    const sectorProjects: Record<string, TreemapProject[]> = {}
    let total = 0

    for (const item of feesBreakdownData) {
      const project = projectsMap.get(item.data_id)
      if (!project) continue

      const feesMetric = item.metrics[METRIC_ID]
      const value = feesMetric?.sum ?? feesMetric?.latest ?? 0
      if (value <= 0) continue

      total += value

      const marketSectorId = project.market_sectors?.[0]
      if (!marketSectorId) continue

      if (!sectorProjects[marketSectorId]) {
        sectorProjects[marketSectorId] = []
      }

      sectorProjects[marketSectorId].push({
        id: project.data_id,
        name: project.name || project.data_id,
        logo: project.logo || "",
        slug: project.slug || project.data_id,
        value,
        percentOfTotal: 0,
        change: (feesMetric?.change ?? 0) * 100,
      })
    }

    const sectorsList: TreemapSector[] = []

    for (const sectorId of DISPLAY_MARKET_SECTORS) {
      const projects = sectorProjects[sectorId] || []
      if (projects.length === 0) continue

      const marketSector = marketSectorsMap.get(sectorId)
      if (!marketSector) continue

      const sortedProjects = projects
        .sort((a, b) => b.value - a.value)
        .slice(0, 6)
        .map((p) => ({
          ...p,
          percentOfTotal: total > 0 ? (p.value / total) * 100 : 0,
        }))

      const sectorTotalValue = sortedProjects.reduce(
        (sum, p) => sum + p.value,
        0
      )

      sectorsList.push({
        id: sectorId,
        name: marketSector.name || sectorId,
        projects: sortedProjects,
        totalValue: sectorTotalValue,
        percentOfTotal: total > 0 ? (sectorTotalValue / total) * 100 : 0,
      })
    }

    const displaySectorIds = new Set(DISPLAY_MARKET_SECTORS)
    const otherProjects: TreemapProject[] = []

    for (const [sectorId, projects] of Object.entries(sectorProjects)) {
      if (
        displaySectorIds.has(
          sectorId as (typeof DISPLAY_MARKET_SECTORS)[number]
        )
      )
        continue

      for (const project of projects) {
        otherProjects.push({
          ...project,
          percentOfTotal: total > 0 ? (project.value / total) * 100 : 0,
        })
      }
    }

    if (otherProjects.length > 0) {
      const sortedOther = otherProjects
        .sort((a, b) => b.value - a.value)
        .slice(0, 4)

      const otherTotalValue = sortedOther.reduce((sum, p) => sum + p.value, 0)

      sectorsList.push({
        id: "other",
        name: "Other",
        projects: sortedOther,
        totalValue: otherTotalValue,
        percentOfTotal: total > 0 ? (otherTotalValue / total) * 100 : 0,
      })
    }

    sectorsList.sort((a, b) => b.totalValue - a.totalValue)

    return { sectors: sectorsList, totalValue: total }
  }, [
    feesBreakdownData,
    projectsData,
    marketSectorsData,
    projectsMap,
    marketSectorsMap,
  ])

  const hasRealData = sectors.length > 0
  const finalSectors = hasRealData ? sectors : FALLBACK_SECTORS
  const finalTotalValue = hasRealData ? totalValue : 1611800000

  return {
    sectors: finalSectors,
    totalValue: finalTotalValue,
    isLoading: isLoading && !hasRealData,
    hasError,
  }
}
